import { PRIVACY_GUIDELINES_TEXT } from "@/constants";
import {
  useCurrentUploadId,
  useCurrentUploadQuery,
  useUploadValidateMotionMedia,
} from "@/hooks/uploads";
import Hyperlink from "@ingka/hyperlink";
import Loading from "@ingka/loading";
import SSRIcon from "@ingka/ssr-icon";
import noticeRedSmall from "@ingka/ssr-icon/paths/notice-red-small";
import Text from "@ingka/text";
import { useShallow } from "zustand/react/shallow";
import { NavigationButtons } from "../NavigationButtons/NavigationButtons";
import OptionalFiles from "./components/video/OptionalFiles";
import Questions from "./components/video/Questions";
import RequiredFiles from "./components/video/RequiredFiles";
import VideoMetadata from "./components/video/VideoMetadata";
import { useStore } from "@/services/store";
import { useMandatoryMediaNavigation } from "./hooks";
import { useAuth } from "@/hooks/auth";
import {
  isLockedForOwner as isLockedForOwnerRule,
  isUploadFinalized,
} from "./formFieldsAccessRules";

const MandatoryVideoMetadata = () => {
  const currentUploadQuery = useCurrentUploadQuery().data;
  const uploadId = useCurrentUploadId();
  const { user } = useAuth();
  const { refetch: validateMotionMedia } =
    useUploadValidateMotionMedia(uploadId);
  const storeAssets = useStore(useShallow((state) => state.videoAssets));

  // Compute isLockedForOwner reactively, matching document/image pattern
  const isLockedForOwner =
    Boolean(currentUploadQuery) &&
    (isLockedForOwnerRule({
      isAdmin: user.isAdmin,
      uploadState: currentUploadQuery!.state,
    }) ||
      isUploadFinalized({ uploadState: currentUploadQuery!.state }));

  const { onPrev, onContinue, isAdminFlow } = useMandatoryMediaNavigation(
    validateMotionMedia,
    isLockedForOwner,
  );

  if (!currentUploadQuery) {
    return <Loading text="Loading current upload request data" />;
  }

  const hasValidationErrors = Object.values(storeAssets).some(
    (asset) => asset?.validationMessage.length > 0,
  );

  return (
    <div className="mx-space100 mt-space300">
      <Text tagName="h2" headingSize="xs" className="mb-space100">
        Accessibility requirements for motion media uploads
      </Text>
      <Text tagName="p" bodySize="s">
        To comply with accessibility legislation, it is mandatory to provide
        additional files such as transcripts and captions for all video assets
        you upload to Ingka DAM. What type of video it is determines which
        additional files are required. The system will prompt you to provide the
        required files. Recommended file types are: .txt for transcripts, .vtt
        for captions and .srt for subtitles. For more information on
        accessibility standards, definitions and how to prepare your video
        content accordingly, please refer to the{" "}
        <Hyperlink
          url="https://www.ikea.net/en/general/accessibility-hub"
          text="Accessibility hub."
          newWindow
        />
      </Text>
      <div className="mt-space250 grid grid-cols-4 gap-x-space150">
        {currentUploadQuery.assets.map((asset, index) => {
          return (
            <div
              className="col-span-4 grid grid-cols-4 gap-x-space150 pb-space200 pt-space200"
              style={{ gridRow: index + 1 }}
              key={asset.id}
            >
              <VideoMetadata
                assetId={asset.id}
                allOriginalConfirmation={
                  currentUploadQuery.allOriginalConfirmation
                }
                fileName={asset.filename}
                isLockedForOwner={isLockedForOwner}
              />
              <Questions
                assetId={asset.id}
                isLockedForOwner={isLockedForOwner}
              />
              <RequiredFiles
                assetId={asset.id}
                isLockedForOwner={isLockedForOwner}
              />
              <OptionalFiles
                assetId={asset.id}
                isLockedForOwner={isLockedForOwner}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-space300 w-[35rem]">
        <Text tagName="p" bodySize="s">
          {PRIVACY_GUIDELINES_TEXT}
        </Text>
      </div>
      <div className="mb-space250 mt-space250 flex flex-row items-center justify-end align-middle">
        {hasValidationErrors && (
          <div className="mr-space100 flex flex-row items-center">
            <SSRIcon
              aria-hidden
              paths={noticeRedSmall}
              className="mt-[0.3rem]"
            />
            <Text tagName="span" className="text-semantic-negative">
              Required input missing
            </Text>
          </div>
        )}
        <NavigationButtons
          onPrev={onPrev}
          disabledNext={hasValidationErrors || isAdminFlow}
          onContinue={onContinue}
        />
      </div>
    </div>
  );
};

export default MandatoryVideoMetadata;
